/**
 * WbDateStrip — horizontale datumkiezer (Routes, Klaarleggen, Bus laden).
 *
 * Knoppen 64 px breed en 56 px hoog, dagnummer mono groot, gekozen dag = inkt
 * op wit omgekeerd, vandaag krijgt een mint-streep. Scrolt bij mount naar
 * `scrollNaar` (standaard de gekozen dag). Zelfde regels als de rest van de set.
 */

import { useEffect, useRef } from 'react';
import clsx from 'clsx';

export interface WbDateStripDag {
  dateStr: string;
  /** Kort label boven het nummer: "Vandaag", "Morgen", "wo". */
  label: string;
  dayNum: number;
  isToday?: boolean;
}

export interface WbDateStripProps {
  dagen: WbDateStripDag[];
  value: string;
  onChange: (dateStr: string) => void;
  /** dateStr om bij mount in beeld te scrollen (standaard `value`). */
  scrollNaar?: string;
  className?: string;
}

const KNOP_BREEDTE = 72; // 64 px + gap

export function WbDateStrip({ dagen, value, onChange, scrollNaar, className }: WbDateStripProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const idx = dagen.findIndex((d) => d.dateStr === (scrollNaar ?? value));
    if (idx < 0) return;
    el.scrollTo({ left: idx * KNOP_BREEDTE - el.offsetWidth / 2 + KNOP_BREEDTE / 2, behavior: 'smooth' });
    // Alleen bij mount: daarna volgt de gebruiker zijn eigen scroll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dagen]);

  return (
    <div ref={ref} className={clsx('overflow-x-auto -mx-3 px-3 hide-scrollbar snap-x snap-mandatory', className)} role="radiogroup" aria-label="Datum">
      <div className="flex gap-2 py-1">
        {dagen.map((d) => {
          const actief = d.dateStr === value;
          return (
            <button
              key={d.dateStr}
              type="button"
              role="radio"
              aria-checked={actief}
              onClick={() => onChange(d.dateStr)}
              className={clsx(
                'wb-focus shrink-0 w-16 min-h-[56px] rounded-wb border-wb snap-center flex flex-col items-center justify-center select-none',
                actief ? 'bg-wb-inkt border-wb-inkt text-white' : 'bg-wb-vlak border-wb-rand text-wb-inkt active:bg-wb-gedempt',
                d.isToday && !actief && 'border-b-4 border-b-wb-mint'
              )}
            >
              <span className={clsx('text-[11px] font-bold uppercase tracking-wide leading-tight', actief ? 'text-wb-kopink2' : 'text-wb-inkt2')}>{d.label}</span>
              <span className="font-mono text-xl font-semibold leading-tight tabular-nums">{d.dayNum}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
